import {
  setReceipt,
  setReceiptAdjustment,
  setReceiptAdjustments,
  setReceiptAdjustmentSelection,
  setReceipts,
  setReceiptsSelection,
  updateReceipt,
} from "../slice/receiptSlice";
import { mutateConsignmentItem, mutateShipmentLine } from "../slice/shipmentSlice";
import { apiSlice } from "./apiSlice";
import { orderApiSlice } from "./orderApiSlice";
import { purchaseOrderApiSlice } from "./purchaseOrderApiSlice";

export const receiptApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    consignmentToReceipt: builder.mutation({
      query: data => ({
        url: "/api_po/receipt/consignment_to_receipt/",
        method: "POST",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(mutateConsignmentItem(result.data)))
          .catch(error => {});
      },
    }),
    createReceiptsFromPO: builder.mutation({
      query: data => ({ url: "/api_po/receipt/po_to_receipt/", method: "POST", body: data }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(() => dispatch(purchaseOrderApiSlice.util.invalidateTags(["purchaseOrderDetail"])))
          .catch(error => {});
      },
    }),
    createReceiptsFromTfOrd: builder.mutation({
      query: data => ({ url: "/api_po/receipt/tf_ord_to_receipt/", method: "POST", body: data }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(() => dispatch(orderApiSlice.util.invalidateTags(["transferOrderDetail"])))
          .catch(error => {});
      },
    }),
    createBillFromReceipt: builder.mutation({
      query: data => ({
        url: "/api_invoice/bill/create_from_receipt/",
        method: "POST",
        body: data,
      }),
    }),
    createReceiptAdjustment: builder.mutation({
      query: data => ({
        url: "/api_po/receipt_adjustment/",
        method: "POST",
        body: data,
      }),
    }),
    updateReceiptAdjustment: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api_po/receipt_adjustment/${id}/`,
        method: "PATCH",
        body: data,
      }),
    }),
    getReceipt: builder.query({
      query: id => `/api_po/receipt/${id}/`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setReceipt(result.data))).catch(error => {});
      },
      providesTags: () => ["receiptDetail"],
    }),
    getReceipts: builder.query({
      query: params => `/api_po/receipt/?${params}`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setReceipts(result.data))).catch(error => {});
      },
      providesTags: () => ["receiptList"],
    }),
    getReceiptsSelection: builder.query({
      query: () => `/api_po/receipt/selection/`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setReceiptsSelection(result.data)))
          .catch(error => {});
      },
    }),
    getPaymentTermDueDate: builder.query({
      query: ({ payment_term_id, start_date }) =>
        `/api_account/payment_term/get_due_date/?payment_term_id=${payment_term_id}&start_date=${start_date}`,
    }),
    getReceiptAdjustments: builder.query({
      query: params => `/api_po/receipt_adjustment/?${params}`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setReceiptAdjustments(result.data)))
          .catch(error => {});
      },
    }),
    getReceiptAdjustment: builder.query({
      query: id => `/api_po/receipt_adjustment/${id}/`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setReceiptAdjustment(result.data)))
          .catch(error => {});
      },
    }),
    getReceiptAdjustmentSelection: builder.query({
      query: () => `/api_po/receipt_adjustment/selection/`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setReceiptAdjustmentSelection(result.data)))
          .catch(error => {});
      },
    }),
    shipmentToReceipt: builder.mutation({
      query: data => ({ url: "/api_po/receipt/shipment_to_receipt/", method: "POST", body: data }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(mutateShipmentLine(result.data))).catch(error => {});
      },
    }),
    updateBulkReceiptLineStatus: builder.mutation({
      query: data => ({
        url: "/api_po/receipt/bulk_update_status/",
        method: "PUT",
        body: data,
      }),
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          const { receipt_line_ids, status } = data;
          if (Array.isArray(receipt_line_ids) && receipt_line_ids.length > 0) {
            dispatch(updateReceipt({ receipt_line_ids: receipt_line_ids, status }));
          }
        } catch (err) {}
      },
      invalidatesTags: (result, error, data) => {
        if (error) return [];
        if (Array.isArray(data?.receipt_ids) && data.receipt_ids.length > 0) {
          return ["receiptList"];
        }
        return [];
      },
    }),
    updateReceiptLineItems: builder.mutation({
      query: ({ id, items_list }) => ({
        url: `/api_po/receipt/${id}/`,
        method: "PATCH",
        body: { items_list },
      }),
      invalidatesTags: ["receiptDetail", "receiptList"],
    }),
  }),
});

export const {
  useConsignmentToReceiptMutation,
  useCreateBillFromReceiptMutation,
  useCreateReceiptAdjustmentMutation,
  useUpdateReceiptAdjustmentMutation,
  useCreateReceiptsFromPOMutation,
  useCreateReceiptsFromTfOrdMutation,
  useGetPaymentTermDueDateQuery,
  useGetReceiptQuery,
  useGetReceiptsQuery,
  useGetReceiptAdjustmentQuery,
  useGetReceiptAdjustmentsQuery,
  useLazyGetReceiptAdjustmentSelectionQuery,
  useGetReceiptsSelectionQuery,
  useLazyGetReceiptsSelectionQuery,
  useShipmentToReceiptMutation,
  useUpdateBulkReceiptLineStatusMutation,
  useUpdateReceiptLineItemsMutation,
} = receiptApiSlice;
